import api from './api';
import {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
  AgentListResponse,
  AgentStats,
} from '../types/agent';
import { AgentVersionHistory, AgentVersionListResponse } from '../types/agent-version';

export const agentService = {
  /**
   * Get all agents with pagination and filters
   */
  async list(params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    search?: string;
  }): Promise<AgentListResponse> {
    const response = await api.get<AgentListResponse>('/agents', {
      params,
    });
    return response.data;
  },

  /**
   * Get a single agent by ID
   */
  async get(id: string): Promise<Agent> {
    const response = await api.get<Agent>(`/agents/${id}`);
    return response.data;
  },

  /**
   * Create a new agent
   */
  async create(data: CreateAgentRequest): Promise<Agent> {
    const response = await api.post<Agent>('/agents', data);
    return response.data;
  },

  /**
   * Update an existing agent
   */
  async update(id: string, data: UpdateAgentRequest): Promise<Agent> {
    const response = await api.patch<Agent>(`/agents/${id}`, data);
    return response.data;
  },

  /**
   * Delete an agent
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/agents/${id}`);
  },

  /**
   * Get agent statistics
   */
  async getStats(): Promise<AgentStats> {
    const response = await api.get<AgentStats>('/agents/stats/summary');
    return response.data;
  },

  /**
   * Activate an agent
   */
  async activate(id: string): Promise<Agent> {
    const response = await api.post<Agent>(`/agents/${id}/activate`);
    return response.data;
  },

  /**
   * Deactivate an agent
   */
  async deactivate(id: string): Promise<Agent> {
    const response = await api.post<Agent>(`/agents/${id}/deactivate`);
    return response.data;
  },

  /**
   * Clone an existing agent
   */
  async clone(id: string, name: string): Promise<Agent> {
    const response = await api.post<Agent>(`/agents/${id}/clone`, { name });
    return response.data;
  },

  /**
   * List version history for an agent
   */
  async listVersions(
    agentId: string,
    params?: { page?: number; limit?: number }
  ): Promise<AgentVersionListResponse> {
    const response = await api.get<AgentVersionListResponse>(
      `/agents/${agentId}/versions`,
      { params }
    );
    return response.data;
  },

  /**
   * Get a specific version of an agent
   */
  async getVersion(agentId: string, version: number): Promise<AgentVersionHistory> {
    const response = await api.get<AgentVersionHistory>(
      `/agents/${agentId}/versions/${version}`
    );
    return response.data;
  },

  /**
   * Rollback an agent to a specific version
   */
  async rollback(agentId: string, version: number, reason?: string): Promise<Agent> {
    const response = await api.post<Agent>(
      `/agents/${agentId}/rollback`,
      { reason },
      { params: { version } }
    );
    return response.data;
  },
};

export default agentService;
